import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Wrench, Eye, EyeOff, MapPin, Globe, Pencil, Zap,
  CheckCircle2, AlertCircle, ChefHat, RefreshCw, Search
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CONTINENTS = ['Africa', 'Americas', 'Asia', 'Europe', 'Oceania', 'Middle East'];

const COUNTRIES = [
  'Albania','Argentina','Australia','Brazil','Canada','Chile','China','Colombia',
  'Egypt','France','Germany','Greece','Hungary','India','Indonesia','Italy',
  'Japan','Latvia','Lebanon','Malaysia','Mexico','Morocco','Peru','Philippines',
  'Portugal','Romania','Russia','South Korea','Spain','Thailand','Tunisia',
  'Turkey','United Kingdom','United States','Vietnam'
];

export const AdminRepairToolPage = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total_needing_repair: 0, total_visible: 0, total_all: 0 });
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState({});
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return {}; }
    return { Authorization: `Bearer ${token}` };
  };

  const fetchRepairQueue = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/recipes/repair-queue`, {
        headers: getAuthHeaders()
      });
      setItems(res.data.repair_items);
      setStats({
        total_needing_repair: res.data.total_needing_repair,
        total_visible: res.data.total_visible,
        total_all: res.data.total_all,
      });
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Session expired');
        navigate('/admin/login');
      } else {
        toast.error('Failed to load repair queue');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRepairQueue(); }, []);

  const applyQuickFix = async (slug, fix) => {
    setFixing(prev => ({ ...prev, [slug + fix.field]: true }));
    try {
      await axios.post(`${API_URL}/api/admin/recipes/${slug}/quick-fix`, fix, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }
      });
      toast.success(`Fixed ${fix.field} for ${slug}`);
      await fetchRepairQueue();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fix failed');
    } finally {
      setFixing(prev => ({ ...prev, [slug + fix.field]: false }));
    }
  };

  const filtered = items.filter(item =>
    !search || item.recipe_name.toLowerCase().includes(search.toLowerCase()) ||
    item.slug.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background p-4 md:p-8" data-testid="repair-tool-page">
      <Helmet><title>Recipe Repair Tool - Admin</title></Helmet>

      {/* Header */}
      <div className="max-w-6xl mx-auto mb-8">
        <Link to="/admin" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-4 text-sm">
          <ArrowLeft className="h-4 w-4" /> Back to Admin
        </Link>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
              <Wrench className="h-6 w-6 text-primary" />
              Recipe Repair Tool
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Diagnose and fix recipes that are not visible on the public site
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchRepairQueue} data-testid="refresh-repair-queue">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="grid grid-cols-3 gap-4">
          <StatCard
            label="Visible on Site"
            value={stats.total_visible}
            icon={<Eye className="h-5 w-5 text-emerald-400" />}
            testId="stat-visible"
          />
          <StatCard
            label="Needing Repair"
            value={stats.total_needing_repair}
            icon={<AlertCircle className="h-5 w-5 text-amber-400" />}
            accent={stats.total_needing_repair > 0}
            testId="stat-needing-repair"
          />
          <StatCard
            label="Total Recipes"
            value={stats.total_all}
            icon={<ChefHat className="h-5 w-5 text-primary" />}
            testId="stat-total"
          />
        </div>
      </div>

      {/* Search */}
      {items.length > 0 && (
        <div className="max-w-6xl mx-auto mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search recipes needing repair..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10 bg-card border-border"
              data-testid="repair-search"
            />
          </div>
        </div>
      )}

      {/* Repair Items */}
      <div className="max-w-6xl mx-auto space-y-4">
        {loading ? (
          <div className="text-center py-16 text-muted-foreground" data-testid="repair-loading">
            Loading repair queue...
          </div>
        ) : filtered.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground" data-testid="all-clear">
                All recipes are healthy
              </h3>
              <p className="text-muted-foreground text-sm mt-1">
                Every recipe passes visibility requirements.
              </p>
            </CardContent>
          </Card>
        ) : (
          filtered.map(item => (
            <RepairCard
              key={item.slug}
              item={item}
              fixing={fixing}
              onQuickFix={applyQuickFix}
              navigate={navigate}
            />
          ))
        )}
      </div>
    </div>
  );
};

function StatCard({ label, value, icon, accent, testId }) {
  return (
    <Card className={`bg-card border-border ${accent ? 'border-amber-500/40' : ''}`}>
      <CardContent className="py-4 flex items-center gap-3">
        {icon}
        <div>
          <p className="text-2xl font-bold text-foreground" data-testid={testId}>{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function RepairCard({ item, fixing, onQuickFix, navigate }) {
  const [countryInput, setCountryInput] = useState('');
  const [continentInput, setContinentInput] = useState('');

  return (
    <Card className="bg-card border-border" data-testid={`repair-card-${item.slug}`}>
      <CardContent className="py-5 px-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          {/* Left: Recipe info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-base font-semibold text-foreground truncate">
                {item.recipe_name}
              </h3>
              <Badge variant="outline" className="text-xs border-amber-500/40 text-amber-400" data-testid={`visibility-status-${item.slug}`}>
                <EyeOff className="h-3 w-3 mr-1" /> Hidden
              </Badge>
              <Badge variant="outline" className="text-xs border-border text-muted-foreground">
                {item.status}
              </Badge>
            </div>

            {/* Geo info */}
            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
              {item.origin_country && (
                <span className="flex items-center gap-1">
                  <Globe className="h-3 w-3" /> {item.origin_country}
                </span>
              )}
              {item.continent && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {item.continent}
                </span>
              )}
            </div>

            {/* Missing fields */}
            <div className="flex flex-wrap gap-2 mb-3">
              {item.missing_fields.map(f => (
                <Badge key={f} className="bg-red-500/15 text-red-400 border-red-500/30 text-xs" data-testid={`missing-${item.slug}-${f}`}>
                  Missing: {f.replace('_', ' ')}
                </Badge>
              ))}
            </div>

            {/* Suggested fixes */}
            <div className="space-y-2">
              {item.suggested_fixes.map(fix => (
                <FixAction
                  key={fix.field}
                  item={item}
                  fix={fix}
                  fixing={fixing}
                  onQuickFix={onQuickFix}
                  navigate={navigate}
                  countryInput={countryInput}
                  setCountryInput={setCountryInput}
                  continentInput={continentInput}
                  setContinentInput={setContinentInput}
                />
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FixAction({ item, fix, fixing, onQuickFix, navigate, countryInput, setCountryInput, continentInput, setContinentInput }) {
  const isFixing = fixing[item.slug + fix.field];

  if (fix.action === 'auto_fill' && fix.value) {
    return (
      <div className="flex items-center gap-2" data-testid={`fix-${item.slug}-${fix.field}`}>
        <Zap className="h-4 w-4 text-amber-400 shrink-0" />
        <span className="text-sm text-muted-foreground">{fix.label}</span>
        <Button
          size="sm"
          variant="outline"
          className="ml-auto h-7 text-xs border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
          disabled={isFixing}
          onClick={() => onQuickFix(item.slug, fix)}
          data-testid={`apply-fix-${item.slug}-${fix.field}`}
        >
          {isFixing ? 'Applying...' : 'Auto-fill'}
        </Button>
      </div>
    );
  }

  if (fix.action === 'select_country') {
    return (
      <div className="flex items-center gap-2" data-testid={`fix-${item.slug}-${fix.field}`}>
        <Globe className="h-4 w-4 text-blue-400 shrink-0" />
        <span className="text-sm text-muted-foreground">Select country:</span>
        <Select
          value={countryInput}
          onValueChange={val => {
            setCountryInput(val);
            onQuickFix(item.slug, { ...fix, value: val });
          }}
        >
          <SelectTrigger className="h-7 text-xs w-48 bg-muted border-border">
            <SelectValue placeholder="Choose country..." />
          </SelectTrigger>
          <SelectContent>
            {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (fix.action === 'select_continent') {
    return (
      <div className="flex items-center gap-2" data-testid={`fix-${item.slug}-${fix.field}`}>
        <MapPin className="h-4 w-4 text-blue-400 shrink-0" />
        <span className="text-sm text-muted-foreground">Select continent:</span>
        <Select
          value={continentInput}
          onValueChange={val => {
            setContinentInput(val);
            onQuickFix(item.slug, { ...fix, value: val });
          }}
        >
          <SelectTrigger className="h-7 text-xs w-48 bg-muted border-border">
            <SelectValue placeholder="Choose continent..." />
          </SelectTrigger>
          <SelectContent>
            {CONTINENTS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (fix.action === 'edit_recipe') {
    return (
      <div className="flex items-center gap-2" data-testid={`fix-${item.slug}-${fix.field}`}>
        <Pencil className="h-4 w-4 text-primary shrink-0" />
        <span className="text-sm text-muted-foreground">{fix.label}</span>
        <Button
          size="sm"
          variant="outline"
          className="ml-auto h-7 text-xs border-primary/40 text-primary hover:bg-primary/10"
          onClick={() => navigate(`/admin/recipes/${item.slug}/edit`)}
          data-testid={`edit-btn-${item.slug}`}
        >
          Open Editor
        </Button>
      </div>
    );
  }

  if (fix.action === 'publish') {
    return (
      <div className="flex items-center gap-2" data-testid={`fix-${item.slug}-${fix.field}`}>
        <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
        <span className="text-sm text-muted-foreground">Ready to publish</span>
        <Button
          size="sm"
          className="ml-auto h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
          disabled={isFixing}
          onClick={() => onQuickFix(item.slug, fix)}
          data-testid={`publish-btn-${item.slug}`}
        >
          {isFixing ? 'Publishing...' : 'Publish Now'}
        </Button>
      </div>
    );
  }

  return null;
}

export default AdminRepairToolPage;
